import type { Unsubscribe } from '@reduxjs/toolkit';
import h from 'hyperscript';

import clearIcon from '@disy/cadenza-icons/clear.svg';
import helpIcon from '@disy/cadenza-icons/help.svg';
import { on } from '@disy/lib/event-util';
import { logger } from '@disy/lib/logging';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { uniqueId } from '@disy/lib/unique-id';
import { createIconButton } from '@disy/ui/button';
import { icon } from '@disy/ui/icon';
import type { MenuContentElement } from '@disy/ui/menu';
import { MENU_ITEM_SEPARATOR, setupMenuButton } from '@disy/ui/menu';
import { createMoreActionsButton } from '@disy/ui/more-actions-button';
import { KEEP_POPUP_OPEN_CLASS } from '@disy/ui/popup';
import { getSetting } from '@disy/workbook.lib/settings';

import { createToggletip } from 'ui/toggletip/toggletip';

import { errorAlert } from 'cadenza/alert';
import { getWorkbookConditionContextSensitiveData } from 'cadenza/api-client/workbook-api';
import type { ConditionElement } from 'cadenza/conditions/condition-element';
import { CONDITION_CHANGE } from 'cadenza/conditions/event-types';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import { isMandatory, supportsReset } from 'cadenza/workbook/conditions/condition-util';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import { ConditionSettingsKey } from 'cadenza/workbook/model/condition-settings-key.gen';
import { ConditionStatus } from 'cadenza/workbook/model/condition-status.gen';
import {
  getLiveModeLastRefreshTime,
  isLiveModeRunning,
} from 'cadenza/workbook/state/workbook-live-mode-selectors.state';
import {
  getActiveWorksheetId,
  getActiveWorksheetViewIds,
  getViewIdsAffectedByCondition,
  getViewsAffectedByCondition,
  getWorkbookNameAndHash,
  isConditionDeactivatedForActiveWorksheet,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { AffectedViewsLabel } from 'cadenza/workbook/workbook-panel/analysis-context/affected-views-label/affected-views-label';
import { CONDITION_DRAG_HANDLE } from 'cadenza/workbook/workbook-panel/analysis-context/condition-drop-panel';

import i18n from './condition.properties';
import './workbook-condition-wrapper.css';

const COMPONENT_NAME = 'd-workbook-condition-wrapper';

/**
 * Wrapper component for the ConditionElement (and its subclasses), specific for use in Workbooks.
 * It adds a header to the condition component with:
 * - Heading text
 * - Reset button
 * - Menu
 * - Affected views widget
 */
export class WorkbookConditionWrapper extends HTMLElement {
  // internal state
  readonly #condition: ConditionDto;
  readonly #withAffectedViews;
  readonly #initiallyFocused;
  readonly #conditionDisabled;

  readonly #menuActionsProvider: (conditionId: ConditionId) => MenuContentElement[];

  // components
  readonly #conditionComponent: ConditionElement;
  #resetButton!: HTMLButtonElement;
  // ! – initialized in constructor
  #label!: HTMLLabelElement;
  #actionsMenuButton?: HTMLButtonElement;
  // ! – initialized in `connectedCallback`
  #otherActionButtons!: HTMLButtonElement[];
  #helpButton?: HTMLButtonElement;
  #affectedViewsLabel?: AffectedViewsLabel;

  #teardowns: Teardown[] = [];
  #teardownLiveModeUpdates?: Unsubscribe;

  /**
   * @param condition - The condition model
   * @param conditionComponent - The ConditionElement (one of its subclasses)
   * @param withAffectedViews - If true the widget that shows the affected views is displayed
   * @param focused - Should it be focused
   * @param menuActionsProvider - If provided, a menu is added in the header using the actions provided by this function
   * @param conditionDisabled - Whether the condition is disabled (depends on the use case, not on the state of the condition)
   * */
  constructor(
    condition: ConditionDto,
    conditionComponent: ConditionElement,
    withAffectedViews = false,
    focused = false,
    menuActionsProvider: (conditionId: ConditionId) => MenuContentElement[] = () => [],
    conditionDisabled = false,
  ) {
    super();
    this.#condition = condition;
    this.#conditionComponent = conditionComponent;
    this.#conditionComponent.conditionLabel = this.#condition.printName;
    this.#conditionComponent.hidden = !conditionDisabled && this.#isConditionDeactivated();
    this.#withAffectedViews = withAffectedViews && !conditionDisabled;
    this.#initiallyFocused = focused;
    this.#conditionDisabled = conditionDisabled;
    this.#menuActionsProvider = menuActionsProvider;
    this.#createConditionHeaderContent();

    on(this.#conditionComponent, CONDITION_CHANGE, () => this.#onConditionUpdate());
  }

  connectedCallback() {
    this.setAttribute('role', 'group');
    const condition = this.#condition;
    this.#otherActionButtons = this.#conditionComponent.getHeaderButtons();
    if (this.#isConditionDeactivated()) {
      this.#otherActionButtons.forEach((button) => {
        button.hidden = true;
      });
    }
    if (
      'applyChangedSettings' in this.#conditionComponent &&
      typeof this.#conditionComponent.applyChangedSettings === 'function'
    ) {
      const applySettings = this.#conditionComponent.applyChangedSettings.bind(this.#conditionComponent);
      this.#teardowns.push(
        ...this.#otherActionButtons.map((button) =>
          on(button, 'change:settings', (e: CustomEvent) => applySettings(e.detail.value)),
        ),
      );
    }
    if (this.#withAffectedViews) {
      this.#teardowns.push(
        observeStore(
          getStore(),
          (state) => getViewsAffectedByCondition(state, condition.id),
          () => this.#updateAffectedAndEnabled(),
        ),
      );
    } else {
      this.#conditionComponent.enabled = !this.#conditionDisabled && condition.status === 'VALID';
      this.#label.classList.toggle('is-disabled', !this.#conditionComponent.enabled);
    }
    if (
      this.#conditionComponent.contextSensitive &&
      this.#conditionComponent.enabled &&
      !this.#isConditionDeactivated()
    ) {
      this.#updateContextSensitiveValues();
    }
    this.classList.add(COMPONENT_NAME, 'condition-wrapper');

    let conditionComponent;
    switch (condition.status) {
      case 'VALID':
        conditionComponent = this.#conditionComponent;
        break;
      case 'FILTER_GEOMETRY_EXCEEDS_CONFIGURED_LIMITS':
        this.#conditionComponent.classList.add('is-invalid');
        conditionComponent = h('div', [
          this.#conditionComponent,
          h('.d-alert.d-alert-small.d-alert-error', ConditionStatus.i18n(condition.status)),
        ]);
        break;
      default:
        conditionComponent = h('.d-alert.d-alert-small.d-alert-error', ConditionStatus.i18n(condition.status));
    }

    this.append(
      h(`.d-condition--header.${CONDITION_DRAG_HANDLE}`, [
        this.#label,
        h('.d-inline-stack', [
          this.#resetButton,
          ...this.#otherActionButtons,
          this.#actionsMenuButton,
          this.#helpButton,
          this.#affectedViewsLabel,
        ]),
      ]),
      conditionComponent,
      ...this.#createHintParagraph(),
    );

    const isConditionSensitiveToLiveMode = ['DateCondition'].includes(condition.type);
    if (isConditionSensitiveToLiveMode) {
      this.#listenToLiveModeEvents();
    }

    this.#onConditionUpdate();

    if (this.#initiallyFocused) {
      this.#conditionComponent.focus();
    }
  }

  #updateAffectedAndEnabled() {
    const state = getStore().getState();
    const { id: conditionId, status: conditionStatus } = this.#condition;
    const affectedViewIds = new Set(getViewIdsAffectedByCondition(state, conditionId));
    const affectedViewIdsOnActiveWorksheet = getActiveWorksheetViewIds(state).filter((viewId) =>
      affectedViewIds.has(viewId),
    );
    this.#conditionComponent.enabled =
      !this.#conditionDisabled && conditionStatus === 'VALID' && affectedViewIdsOnActiveWorksheet.length > 0;
    this.#label.classList.toggle('is-disabled', !this.#conditionComponent.enabled);
  }

  disconnectedCallback() {
    // Moved "innerHTML = ''" to disconnectedCallback due to problems with IE when
    // drag and dropping conditions. In IE upon dropping condition for split second
    // there will be double input fields visible.
    this.innerHTML = '';

    this.#teardowns = tearDown(this.#teardowns);
    this.#teardownLiveModeUpdates = tearDown(this.#teardownLiveModeUpdates);
  }

  #createHintParagraph() {
    const hint = getSetting<string>(this.#condition.settings, ConditionSettingsKey.toPersistenceKey('CONDITION_HINT'));
    if (hint?.length && !this.#conditionComponent.hidden) {
      return [h(`d-truncated-p.${COMPONENT_NAME}--hint`, { fullText: hint })];
    }
    return [];
  }

  #listenToLiveModeEvents() {
    this.#teardowns.push(
      observeStore(getStore(), isLiveModeRunning, (isRunning) => {
        this.#teardownLiveModeUpdates = tearDown(this.#teardownLiveModeUpdates);
        if (isRunning) {
          // Subscribe to live mode refresh events to update relative date conditions
          this.#teardownLiveModeUpdates = observeStore(getStore(), getLiveModeLastRefreshTime, () => {
            this.#conditionComponent.dispatchEvent(new CustomEvent('LiveModeRefresh'));
            this.#refreshConditionValues();
          });
        }
      }),
    );
  }

  #createConditionHeaderContent() {
    this.#label = this.#createLabel(this.#condition.status === 'VALID', isMandatory(this.#condition));
    this.#actionsMenuButton = this.#createActionsMenuButton();
    if (!this.#conditionDisabled && this.#isConditionDeactivated()) {
      this.#label.classList.add('is-deactivated');
    } else {
      this.#resetButton = this.#createResetButton();
      this.#helpButton = this.#conditionComponent.hasHelpContent ? this.#createHelpButton() : undefined;
    }
    if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-show-filtered-views')) {
      return;
    }
    try {
      if (this.#withAffectedViews) {
        this.#affectedViewsLabel = this.#createAffectedViewsLabel(this.#condition.id);
      }
    } catch (e) {
      logger.error('Failed during creation of affected views label', e);
    }
  }

  #createLabel(valid: boolean, mandatory: boolean) {
    const isRequired = mandatory ? '.is-required' : '';
    const id = `${this.#condition.name}--label`;
    const label = h<HTMLLabelElement>(
      `label.d-condition--name${isRequired}`,
      {
        id,
        onclick: () => this.#conditionComponent.focus(),
      },
      valid ? this.#condition.printName : i18n('invalid.condition.title'),
    );

    label.classList.toggle('is-invalid', !valid);
    this.setAttribute('aria-labelledby', id);
    this.#conditionComponent.labelledBy = label;
    return label;
  }

  #createResetButton() {
    return createIconButton(icon(clearIcon), i18n('clear'), {
      variant: 'borderless',
      size: 'xs',
      styleClass: ['d-condition--clear', 'is-hover-visible'],
      hidden: !(supportsReset(this.#condition) && this.#conditionComponent.changed),
      onclick: this.#onResetButtonClick.bind(this),
      attrs: {
        'aria-label': `${this.#condition.printName} - ${i18n('clear')}`,
      },
    });
  }

  #onResetButtonClick() {
    this.#conditionComponent.focus();
    this.#conditionComponent.reset();
    this.#resetButton.hidden = true;
  }

  #onConditionUpdate() {
    if (this.#resetButton) {
      this.#resetButton.hidden = !this.#conditionComponent.changed || !this.#conditionComponent.enabled;
    }
    this.#conditionComponent.reportValidity();
  }

  #createAffectedViewsLabel(conditionId: ConditionId) {
    const label = new AffectedViewsLabel({ conditionId });
    label.id = uniqueId();
    this.#conditionComponent.describedBy = label;
    return label;
  }

  #createActionsMenuButton() {
    const hasActions = this._createMenuActions().length > 0;
    if (hasActions) {
      const actionsButton = createMoreActionsButton(this.#condition.printName, { size: 'xs' });
      actionsButton.classList.add('has-own-menu'); // prevent generic menu handling, see AttributeDropPanel
      setupMenuButton(actionsButton, this._createMenuActions.bind(this));
      return actionsButton;
    }
  }

  #createHelpButton() {
    return createToggletip({
      icon: icon(helpIcon),
      toggletipContent: this.#conditionComponent.helpContentProvider,
      styleClass: 'is-hover-visible',
    });
  }

  /**
   * Menu actions are a combination of context-sensitive actions that are injected via the provider,
   * and specific (context-insensitive) actions provided by the condition component itself.
   *
   * If the created array is empty, the menu will not be created.
   *
   * @see ConditionElement.getSpecificActionsForWorkbooks
   * @return Array of menu actions
   * @private
   */
  _createMenuActions() {
    const content: MenuContentElement[] = [];
    if (!this.#isConditionDeactivated()) {
      const specificActionsForWorkbooks = this.#conditionComponent.getSpecificActionsForWorkbooks();

      content.push(...specificActionsForWorkbooks);
      if (content.length) {
        content.push(MENU_ITEM_SEPARATOR);
      }
    }
    content.push(...this.#menuActionsProvider(this.#condition.id));
    return content;
  }

  #updateContextSensitiveValues() {
    this.#conditionComponent.classList.remove('is-invalid');
    getWorkbookConditionContextSensitiveData(
      getWorkbookNameAndHash(getStore().getState()),
      getActiveWorksheetId(getStore().getState()),
      this.#condition.id,
    )
      .then((data) => {
        this.#conditionComponent.contextSensitiveValues = data;
        this.#otherActionButtons.forEach((b) => {
          b.style.visibility = 'visible';
        });
      })
      .catch((err) => {
        logger.error('Failed to update context sensitive data', err);
        errorAlert(i18n('condition.query.error.title'), i18n('condition.query.error.message'), {
          styleClass: KEEP_POPUP_OPEN_CLASS,
        });
        this.#conditionComponent.classList.add('is-invalid');
      });
  }

  #refreshConditionValues() {
    this.#conditionComponent.classList.remove('is-invalid');
    getWorkbookConditionContextSensitiveData(
      getWorkbookNameAndHash(getStore().getState()),
      getActiveWorksheetId(getStore().getState()),
      this.#condition.id,
    )
      .then((data) => {
        this.#conditionComponent.contextSensitiveValues = data;
      })
      .catch((err) => {
        logger.error('Failed to refresh condition values', err);
        errorAlert(i18n('condition.query.error.title'), i18n('condition.query.error.message'));
        this.#conditionComponent.classList.add('is-invalid');
      });
  }

  #isConditionDeactivated() {
    return isConditionDeactivatedForActiveWorksheet(getStore().getState(), this.#condition.id);
  }
}

customElements.define(COMPONENT_NAME, WorkbookConditionWrapper);
